import { type ReactNode, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useAgencyProfile } from '@/hooks/useAgencyProfile'
import { toDate } from '@/lib/firebase/firestore'
import { createCheckoutSession } from '@/services/billing'
import { BILLING_ENABLED } from '@/lib/featureFlags'
import { AlertTriangle, CreditCard, Shield, Loader2 } from 'lucide-react'
import { Button, Spinner } from '@/components/ui'

interface SubscriptionGuardProps {
  children: ReactNode
}

export default function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { agency, isLoading } = useAgencyProfile(user?.agencyId)
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)

  // Always allow access to settings (so users can upgrade billing)
  if (location.pathname.startsWith('/settings')) return <>{children}</>

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <Spinner size="lg" />
      </div>
    )
  }

  // No agency data — let them through (shouldn't happen)
  if (!agency) return <>{children}</>

  const subscription = agency.subscription
  if (!subscription) return <>{children}</>

  // Active or paid subscriptions — let them through
  if (subscription.status === 'active') return <>{children}</>

  // Trial — check if expired
  if (subscription.status === 'trial') {
    const trialEnd = toDate(subscription.trialEndsAt)
    if (trialEnd && trialEnd.getTime() > Date.now()) {
      // Trial still active
      return <>{children}</>
    }
    // Trial expired — block
  }

  // Cancelled or past_due — also block
  const isExpiredTrial = subscription.status === 'trial'
  const isCancelled = subscription.status === 'cancelled'
  const isPastDue = subscription.status === 'past_due'

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
      <div className="w-full max-w-lg text-center space-y-6">
        {/* Icon */}
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-warning-100 dark:bg-warning-900/30">
          {isExpiredTrial ? (
            <AlertTriangle className="h-10 w-10 text-warning-600 dark:text-warning-400" />
          ) : (
            <Shield className="h-10 w-10 text-warning-600 dark:text-warning-400" />
          )}
        </div>

        {/* Title */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isExpiredTrial
              ? 'Your Free Trial Has Ended'
              : isCancelled
              ? 'Subscription Cancelled'
              : isPastDue
              ? 'Payment Past Due'
              : 'Subscription Required'}
          </h1>
          <p className="mt-3 text-gray-600 dark:text-gray-400">
            {isExpiredTrial
              ? 'Your 30-day free trial has expired. Upgrade to continue using AsiliFlow — all your data is safe and waiting.'
              : isCancelled
              ? 'Your subscription has been cancelled. Reactivate to regain access to your agency data.'
              : isPastDue
              ? 'Your payment is past due. Please update your payment method to continue using AsiliFlow.'
              : 'A subscription is required to use AsiliFlow.'}
          </p>
        </div>

        {/* Features reminder */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-left">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            AsiliFlow Pro — $299/month
          </p>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-center gap-2">
              <span className="text-primary-500">✓</span> Unlimited clients & Caregivers
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary-500">✓</span> AI-powered shift notes
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary-500">✓</span> Real-time shift management
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary-500">✓</span> Family portal
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary-500">✓</span> Compliance & incident tracking
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={async () => {
              if (!BILLING_ENABLED) {
                navigate('/dashboard')
                return
              }
              setIsCheckoutLoading(true)
              try {
                const data = await createCheckoutSession(window.location.origin)
                if (data.url) {
                  window.location.assign(data.url)
                  return
                }
              } catch {
                // Fallback to billing page
              }
              setIsCheckoutLoading(false)
              navigate(BILLING_ENABLED ? '/settings?tab=billing' : '/dashboard')
            }}
            disabled={isCheckoutLoading}
            className="w-full"
          >
            {isCheckoutLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CreditCard className="h-4 w-4 mr-2" />
            )}
            {isCheckoutLoading ? 'Redirecting to checkout...' : isExpiredTrial ? 'Upgrade Now' : 'Manage Billing'}
          </Button>
          <button
            onClick={signOut}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
